'use client';

import {useCallback} from 'react';
import {TrackImpression, publishEvent} from '@udemy-v2/event-tracking';

import {buildPresentedEvent} from '@/components/tracking/instructor-community-events';

interface CommunitySectionImpressionProps {
    /** Profile owner's user id. */
    instructorId?: string | null;
    /** Community id from the community read. */
    communityId?: string | null;
    children: React.ReactNode;
}

/**
 * Fires `InstructorCommunityPresented` when the community section comes into
 * view, using the library's `TrackImpression` with its default thresholds
 * (`visibilityThreshold` 0.99, `minDuration` 500ms cumulative, `onlyOnce`) —
 * the other community surfaces use the same defaults, so the resulting CTRs
 * stay comparable.
 *
 * ⚠️ Wraps the community section's **header division**, not the whole
 * `<section>`. Two reasons, both structural:
 *
 * 1. The full section (header + intro + benefits + up to three feature banners)
 *    is taller than the viewport, so its intersection ratio never reaches the
 *    0.99 threshold and the impression would never fire. The header is the top
 *    of the section and comfortably shorter than one screen.
 * 2. `TrackImpression` renders a wrapper `<div>`. Around the `<section>` that
 *    div would become the flex child of `.main` and break the subscribed
 *    layout's `order: 1` reorder; inside `.header-shell` (a plain block) it is
 *    layout-neutral.
 *
 * Renders children unwrapped — no observer, no event — when either id is
 * missing, which is the instructor DRAFT preview path.
 */
export function CommunitySectionImpression({
    instructorId,
    communityId,
    children,
}: CommunitySectionImpressionProps) {
    const trackFunc = useCallback(() => {
        const event = buildPresentedEvent({instructorId, communityId});
        if (event) {
            publishEvent(event);
        }
    }, [instructorId, communityId]);

    if (!instructorId || !communityId) {
        return <>{children}</>;
    }

    return <TrackImpression trackFunc={trackFunc}>{children}</TrackImpression>;
}
